import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { I } from '@/shared/icons';
import { toApiError, useApi, type PropertyResponse } from '@/shared/api';
import { propertyKeys } from '../queries';
import { buildPreflightChecks, preflightPasses } from './channexPreflight';

interface ChannexPreflightDialogProps {
  property: PropertyResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onQueued?: () => void;
}

export function ChannexPreflightDialog({
  property,
  open,
  onOpenChange,
  onQueued,
}: ChannexPreflightDialogProps) {
  const api = useApi();
  const queryClient = useQueryClient();

  const group = useQuery({
    queryKey: ['property-groups', property.propertyGroupId],
    queryFn: () => api.propertyGroups.get(property.propertyGroupId as string),
    enabled: open && !!property.propertyGroupId,
  });

  const checks = buildPreflightChecks(property, group.data);
  const ready = preflightPasses(checks) && !group.isLoading;

  const publish = useMutation({
    mutationFn: () => api.channex.provisionProperty(property.id),
    onSuccess: async () => {
      onQueued?.();
      await queryClient.invalidateQueries({ queryKey: propertyKeys.detail(property.id) });
      toast.success('Objekt-Veröffentlichung wurde eingeplant.');
      onOpenChange(false);
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Objekt bei Channex veröffentlichen</DialogTitle>
          <DialogDescription>
            Voraussetzungen prüfen und die Objekt-Property zur Verarbeitung einplanen. Die
            Apartments werden in einem folgenden Schritt als Zimmer und Rate veröffentlicht.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ul className="space-y-2">
            {checks.map((check) => (
              <li key={check.key} className="flex items-start gap-3">
                <span
                  className={
                    check.ok
                      ? 'bg-sage/15 text-sage mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full'
                      : 'bg-sealwax/15 text-sealwax mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full'
                  }
                  aria-hidden
                >
                  <I name={check.ok ? 'Check' : 'Close'} size={12} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink text-sm font-medium">{check.label}</p>
                  <p className={check.ok ? 'text-ink-faint text-xs' : 'text-sealwax text-xs'}>
                    {check.detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={publish.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="brand"
            loading={publish.isPending}
            disabled={!ready || publish.isPending}
            onClick={() => publish.mutate()}
          >
            Objekt einplanen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
